import type { DestroyOptions } from 'pixi.js';
import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { AppStateController } from '../../app/state/AppStateController';
import { soundManager } from '../../app/SoundManager';
import { applyLayoutState } from '../../libs/layouts/populateLayouts/applyLayoutState';
import { triggerParticleFX } from '../../libs/particles/triggerParticleFX';
import { ShopFreeGiftOfferLayout } from '../../../layouts/mainMenu/shop/ShopFreeGiftOfferLayout';
import { ShopItemRewardLayout } from '../../../layouts/mainMenu/shop/ShopItemRewardLayout';
import { formatAmountForInventory, formatInventoryCountdown, setIconForInventory } from './utils/setIconForInventory';
import { InventoryItem } from '../../app/resources/types';
import { animateColorTo } from '../../utils/pixi/animations/filter-animation-utils';
import { playLayoutAnimation } from '../../libs/layouts/layoutAnimations/playLayoutAnimation';

export class ShopFreeGiftOfferView extends Container {
  public readonly onFreeGiftClaimed = new Signal();

  public readonly onFreeGiftAnimationFinished = new Signal();

  public readonly onFreeGiftRefreshed = new Signal();

  private readonly offerLayout = new ShopFreeGiftOfferLayout();

  constructor(
    private readonly state: AppStateController,
    private readonly itemType: InventoryItem,
    private readonly amount: number,
  ) {
    super();
    this.init();
  }

  private init(): void {
    const {
      particleSlot,
      root, 
      rays,
      raysMask,
      countdownText,
    } = this.offerLayout;

    root.props({
      parent: this,
    });

    rays.mask = raysMask;

    void triggerParticleFX('Shop_Gift_Particles', particleSlot);
    const nextGiftDate = this.state.getNextFreeShopGiftDate();
    if (nextGiftDate > 0) {
      applyLayoutState(this.offerLayout, 'locked');

      formatInventoryCountdown(countdownText, nextGiftDate);

      const interval = setInterval(() => {
        formatInventoryCountdown(countdownText, nextGiftDate);
        if (Date.now() > nextGiftDate) {
          this.onFreeGiftRefreshed.emit();
          clearInterval(interval);
        }
      }, 200);
      this.once('destroyed', () => {
        clearInterval(interval);
      });
    } else {
      if (!this.state.hasSeenFreeShopGiftToday()) {
        void playLayoutAnimation(this.offerLayout, 'Idle', true);
      }
      root.onpointertap = async (): Promise<void> => {
        root.onpointertap = undefined;
        this.onFreeGiftClaimed.emit();
        await this.animateGiftOpen();
        this.onFreeGiftAnimationFinished.emit();
      };
    }
  }

  public destroy(options?: DestroyOptions): void {
    this.onFreeGiftClaimed.disconnectAll();
    this.onFreeGiftAnimationFinished.disconnectAll();
    this.onFreeGiftRefreshed.disconnectAll();
    this.offerLayout.root.onpointertap = null;
    super.destroy(options);
  }

  private async animateGiftOpen(): Promise<void> {
    const promises: Promise<void>[] = [];

    const { countdownText, itemSlot, notificationDot } = this.offerLayout;

    notificationDot.visible = false;

    const item = new ShopItemRewardLayout();
    item.root.props({
      parent: itemSlot,
      scaleXY: 0,
    });

    const iconOverrides = { [InventoryItem.Coin]: 'Coins_2' };
    setIconForInventory(item.icon, this.itemType, iconOverrides);
    formatAmountForInventory(item.amountText, this.itemType, this.amount);
    formatInventoryCountdown(countdownText, this.state.getNextFreeShopGiftDate());

    await playLayoutAnimation(this.offerLayout, 'Open', false, async (event) => {
      if (event === 'spawnGift') {
        soundManager.play('Gameplay_Match_3');
        promises.push(playLayoutAnimation(item, 'PopOut'));
        promises.push(animateColorTo(item.icon, 0xffffff, 0, 1, 300, 'quadOut', 100, 0, true).asyncOnComplete());
      }
    });

    await Promise.all(promises);
  }
}
